import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Spin,
  Select,
  Row,
  Col,
  Card,
  Timeline,
  Divider,
  Avatar,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import ResizablePane from '../components/ResizablePane';
import {
  getContacts,
  updateContact,
  createContact,
  addContactComment,
} from '../services/contactsService';

const { Title, Text } = Typography;
const { TextArea } = Input;

function ContactPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [form] = Form.useForm();
  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (id) {
      fetchContact();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchContact = async () => {
    try {
      const contacts = await getContacts();
      const foundContact = contacts.find(c => c.id === id);
      if (foundContact) {
        setContact(foundContact);
        form.setFieldsValue({
          first_name: foundContact.first_name,
          last_name: foundContact.last_name,
          email: foundContact.email,
          phone: foundContact.phone,
          company: foundContact.company,
          job_title: foundContact.job_title,
          type: foundContact.type,
          status: foundContact.status,
          source: foundContact.source,
          address: foundContact.address,
          website: foundContact.website,
          notes: foundContact.notes,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async values => {
    setLoading(true);
    try {
      if (id) {
        await updateContact(id, values);
      } else {
        await createContact(values);
      }
      navigate('/admin/contacts');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addContactComment(id, newComment);
      setNewComment('');
      await fetchContact();

      // Scroll to bottom after new comment is added
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    // Scroll to bottom when comments change
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [contact?.contact_comments]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={2}>{id ? 'Edit Contact' : 'New Contact'}</Title>
          <Button onClick={() => navigate('/admin/contacts')}>Back to Contacts</Button>
        </Space>
      </div>

      {id ? (
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: 'calc(100vh - 199px)',
            flex: 1,
          }}
        >
          <div style={{ flex: 1, marginRight: '24px' }}>
            <Card>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please input the first name!',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please input the last name!',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="email" label="Email">
                      <Input type="email" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="phone" label="Phone">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="company" label="Company">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="job_title" label="Job Title">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="type"
                      label="Type"
                      rules={[{ required: true, message: 'Please select a type!' }]}
                    >
                      <Select>
                        <Select.Option value="client">Client</Select.Option>
                        <Select.Option value="vendor">Vendor</Select.Option>
                        <Select.Option value="partner">Partner</Select.Option>
                        <Select.Option value="lead">Lead</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="status"
                      label="Status"
                      rules={[{ required: true, message: 'Please select a status!' }]}
                    >
                      <Select>
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                        <Select.Option value="archived">Archived</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="source"
                      label="Source"
                      rules={[{ required: true, message: 'Please select a source!' }]}
                    >
                      <Select>
                        <Select.Option value="website">Website</Select.Option>
                        <Select.Option value="referral">Referral</Select.Option>
                        <Select.Option value="direct">Direct</Select.Option>
                        <Select.Option value="event">Event</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="address" label="Address">
                  <TextArea rows={2} />
                </Form.Item>

                <Form.Item name="website" label="Website">
                  <Input />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <TextArea rows={3} />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => navigate('/admin/contacts')}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      {id ? 'Update Contact' : 'Create Contact'}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </div>
          <div style={{ flex: 1, borderRadius: '12px', overflow: 'hidden' }}>
            <Card
              title="Comments"
              bodyStyle={{
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 250px)',
              }}
            >
              <div
                ref={chatContainerRef}
                className="chat-container"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '12px',
                }}
              >
                {contact?.contact_comments
                  ?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                  ?.map((comment, index) => (
                    <div
                      key={comment.id || index}
                      className="chat-message"
                      style={{
                        marginBottom: '16px',
                        maxWidth: '80%',
                        alignSelf: index % 2 === 0 ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        {index % 2 === 0 ? (
                          <Avatar
                            size="small"
                            style={{
                              marginRight: '8px',
                              backgroundColor: '#87d068',
                            }}
                            icon={<UserOutlined />}
                          >
                            {comment.user_email
                              ? comment.user_email.substring(0, 2).toUpperCase()
                              : 'U'}
                          </Avatar>
                        ) : null}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#1890ff',
                              color: index % 2 === 0 ? 'inherit' : 'white',
                              borderRadius: '12px',
                              padding: '12px 16px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            }}
                          >
                            <div>{comment.comment}</div>
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#8c8c8c',
                              marginTop: '4px',
                              textAlign: index % 2 === 0 ? 'left' : 'right',
                            }}
                          >
                            {index % 2 === 0 ? (
                              <span>
                                {comment.user_email || 'User'} •{' '}
                                {new Date(comment.created_at).toLocaleString()}
                              </span>
                            ) : (
                              <span>{new Date(comment.created_at).toLocaleString()} • You</span>
                            )}
                          </div>
                        </div>
                        {index % 2 === 1 ? (
                          <Avatar
                            size="small"
                            style={{
                              marginLeft: '8px',
                              backgroundColor: '#1890ff',
                            }}
                          >
                            You
                          </Avatar>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
              <div
                style={{
                  padding: '12px',
                  borderTop: '1px solid #f0f0f0',
                  backgroundColor: '#fff',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <TextArea
                    rows={2}
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Type a message..."
                    style={{
                      borderRadius: '18px',
                      padding: '10px 14px',
                      resize: 'none',
                      flexGrow: 1,
                      marginRight: '8px',
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey && newComment.trim()) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    shape="circle"
                    icon={<span>➤</span>}
                    style={{ marginBottom: '4px' }}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      ) : (
        <Row gutter={24}>
          <Col span={24}>
            <Card>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="first_name"
                      label="First Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please input the first name!',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="last_name"
                      label="Last Name"
                      rules={[
                        {
                          required: true,
                          message: 'Please input the last name!',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="email" label="Email">
                      <Input type="email" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="phone" label="Phone">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="company" label="Company">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="job_title" label="Job Title">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="type"
                      label="Type"
                      rules={[{ required: true, message: 'Please select a type!' }]}
                    >
                      <Select>
                        <Select.Option value="client">Client</Select.Option>
                        <Select.Option value="vendor">Vendor</Select.Option>
                        <Select.Option value="partner">Partner</Select.Option>
                        <Select.Option value="lead">Lead</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="status"
                      label="Status"
                      rules={[{ required: true, message: 'Please select a status!' }]}
                    >
                      <Select>
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                        <Select.Option value="archived">Archived</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="source"
                      label="Source"
                      rules={[{ required: true, message: 'Please select a source!' }]}
                    >
                      <Select>
                        <Select.Option value="website">Website</Select.Option>
                        <Select.Option value="referral">Referral</Select.Option>
                        <Select.Option value="direct">Direct</Select.Option>
                        <Select.Option value="event">Event</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="address" label="Address">
                  <TextArea rows={2} />
                </Form.Item>

                <Form.Item name="website" label="Website">
                  <Input />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <TextArea rows={3} />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Space>
                    <Button onClick={() => navigate('/admin/contacts')}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      {id ? 'Update Contact' : 'Create Contact'}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default ContactPage;
